import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/api";
import { Star, Plus, Minus } from "lucide-react";

export default function ProductCard({ product }) {
  const { cart, add, update } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const inCart = cart?.items?.find((i) => i.product_id === product.product_id);
  const qty = inCart?.quantity || 0;
  const discount = Math.max(0, Math.round(((product.mrp - product.price) / product.mrp) * 100));

  const handleAdd = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user) { toast.error("Please sign in to add items"); navigate("/login"); return; }
    setBusy(true);
    try { await add(product.product_id, 1); toast.success(`${product.name} added`); }
    catch { toast.error("Could not add"); }
    finally { setBusy(false); }
  };

  const handleQty = async (e, next) => {
    e?.preventDefault();
    e?.stopPropagation();
    setBusy(true);
    try { await update(product.product_id, next); }
    finally { setBusy(false); }
  };

  return (
    <div className="vm-card overflow-hidden flex flex-col group" data-testid={`product-card-${product.product_id}`}>
      <Link to={`/product/${product.product_id}`} className="block relative aspect-square bg-[#F4F6F8] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {discount > 0 && (
          <span className="absolute top-2 left-2 bg-grass-500 text-white text-[10px] font-extrabold px-2 py-1 rounded-md tracking-wide">
            {discount}% OFF
          </span>
        )}
      </Link>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{product.brand}</p>
        <Link to={`/product/${product.product_id}`} className="block">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2 text-[#0F1115] hover:text-grass-600 transition-colors">
            {product.name}
          </h3>
        </Link>
        <span className="flex items-center gap-1 text-[11px] text-gray-500 font-medium">
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {product.rating} • {product.stock > 0 ? "In stock" : "Out of stock"}
        </span>

        <div className="mt-auto pt-2 flex items-end justify-between gap-2">
          <div className="leading-tight">
            <p className="font-display text-lg font-extrabold text-[#0F1115]">{inr(product.price)}</p>
            {product.mrp > product.price && (
              <p className="text-[11px] text-gray-400 line-through">{inr(product.mrp)}</p>
            )}
          </div>
          {qty === 0 ? (
            <button
              className="vm-add-pill"
              onClick={handleAdd}
              disabled={busy || product.stock === 0}
              data-testid={`product-add-${product.product_id}`}
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          ) : (
            <div className="flex items-center gap-1 bg-grass-500 text-white rounded-lg px-1.5 py-1 shadow-soft">
              <button
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-grass-600"
                onClick={(e) => handleQty(e, qty - 1)}
                data-testid={`product-decrease-${product.product_id}`}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-5 text-center text-xs font-bold" data-testid={`product-qty-${product.product_id}`}>{qty}</span>
              <button
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-grass-600"
                onClick={(e) => handleQty(e, qty + 1)}
                data-testid={`product-increase-${product.product_id}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
